import * as THREE from 'three'
import Experience from '../Experience.js'

import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'

const TONE_MAPPING_MAP = {
    'NoToneMapping': THREE.NoToneMapping,
    'NeutralToneMapping': THREE.NeutralToneMapping,
    'LinearToneMapping': THREE.LinearToneMapping,
    'ReinhardToneMapping': THREE.ReinhardToneMapping,
    'CineonToneMapping': THREE.CineonToneMapping,
    'ACESFilmicToneMapping': THREE.ACESFilmicToneMapping,
    'AgXToneMapping': THREE.AgXToneMapping,
};

export default class Renderer {
    constructor(settings = {}) {
        this.experience = new Experience()
        this.canvas = this.experience.canvas
        this.resizer = this.experience.resizer
        this.scene = this.experience.scene
        this.camera = this.experience.camera

        this.bloom = settings.bloom_enabled || false;

        this.setInstance(settings)
        this.setPostProcessing(settings)
    }

    setInstance(settings) {
        this.instance = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true
        })
        this.instance.toneMappingExposure = settings.tone_mapping_exposure ?? 1;
        this.instance.toneMapping = TONE_MAPPING_MAP[settings.tone_mapping] ?? THREE.NeutralToneMapping;

        this.instance.outputColorSpace = THREE.SRGBColorSpace;
        this.instance.shadowMap.enabled = true
        this.instance.shadowMap.type = THREE.PCFSoftShadowMap
        this.instance.setClearColor(settings.clear_color || '#211d20')
        this.instance.setSize(this.resizer.currentWidth, this.resizer.currentHeight)
        this.instance.setPixelRatio(this.resizer.pixelRatio)
    }

    setPostProcessing(settings) {
        this.composer = new EffectComposer(this.instance)
        this.composer.setSize(this.resizer.currentWidth, this.resizer.currentHeight)
        this.composer.setPixelRatio(this.resizer.pixelRatio, 0.1)

        const renderPass = new RenderPass(this.scene, this.camera.instance)
        this.composer.addPass(renderPass)

        this.bloomPass = new UnrealBloomPass(
            new THREE.Vector2(this.resizer.currentWidth, this.resizer.currentHeight),
            settings.bloom_strength ?? 0.15,
            settings.bloom_radius ?? 0.1,
            settings.bloom_threshold ?? 0.1
        )
        this.composer.addPass(this.bloomPass)
    }

    applySettings(settings) {
        const hasValue = (value) => value !== undefined && value !== null;

        if (hasValue(settings.tone_mapping)) {
            this.instance.toneMapping = TONE_MAPPING_MAP[settings.tone_mapping] ?? THREE.NeutralToneMapping;
        }
        if (hasValue(settings.tone_mapping_exposure)) {
            this.instance.toneMappingExposure = settings.tone_mapping_exposure;
        }
        if (hasValue(settings.clear_color)) {
            this.instance.setClearColor(settings.clear_color);
        }
        if (hasValue(settings.bloom_enabled)) {
            this.bloom = settings.bloom_enabled;
        }
        if (this.bloomPass) {
            if (hasValue(settings.bloom_strength)) this.bloomPass.strength = settings.bloom_strength;
            if (hasValue(settings.bloom_radius)) this.bloomPass.radius = settings.bloom_radius;
            if (hasValue(settings.bloom_threshold)) this.bloomPass.threshold = settings.bloom_threshold;
        }
    }

    resize() {
        const minHeight = this.resizer.minimumHeight;
        const height = Math.max(this.resizer.currentHeight, minHeight);
        const width = this.resizer.currentWidth;
        this.instance.setSize(width, height);
        this.instance.setPixelRatio(this.resizer.pixelRatio);
        this.composer.setSize(width, height);
        this.composer.setPixelRatio(this.resizer.pixelRatio);
    }

    updateCamera(camera) {
        // Update the RenderPass to use the new camera after a camera type switch
        for (const pass of this.composer.passes) {
            if (pass.camera) pass.camera = camera;
        }
    }

    update() {
        this.instance.render(this.scene, this.camera.instance);
        if (this.bloom) {
            this.composer.render();
        }
    }
}
